import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import i18n from "../../lib/i18n";
import { createEntitiesConsumer } from "../../lib/entities";
import { EntityValidationResult } from "../../lib/entities/validation";
import { User, CompanyMembership, CompanyMembershipSection } from "../entities";
import { RootState } from "../../state";
import MembershipSectionCover from "./MembershipSectionCover";
import MembershipSectionEditPopup from "./MembershipSectionEditPopup";
import TogglableStyledInput from "../../common/components/TogglableStyledInput";
import { media, colors } from "../../common/utils/style";
import Translate from "../../common/components/Translate";
import ErrorMessage from "../../common/components/ErrorMessage";

interface OwnProps {
  section: CompanyMembershipSection;
  memberships: CompanyMembership[];
  error?: EntityValidationResult<CompanyMembershipSection>;
  editable: boolean;
  onChange?(section: CompanyMembershipSection): void;
  onDelete?(section: CompanyMembershipSection): void;
}

interface MappedProps {
  locale: string;
}

type Props = OwnProps & MappedProps;

const UsersConsumer = createEntitiesConsumer<{ users: User[] }>({
  users: "users",
});

const MaxMemberLength = 4;

const showMembers = (members: User[]) => {
  return members.slice(0, MaxMemberLength);
};

class MembershipSectionHeader extends React.PureComponent<Props> {
  public onChangeSectionName = (text: string) => {
    const { section, onChange } = this.props;
    if (onChange) {
      onChange({ ...section, name: text });
    }
  };

  public onChangeSectionDescription = (text: string) => {
    const { section, onChange } = this.props;
    if (onChange) {
      onChange({ ...section, description: text });
    }
  };

  public render() {
    const { memberships } = this.props;
    return <UsersConsumer query={{ users: memberships.map(m => m.user) }}>{this.renderWithUsers}</UsersConsumer>;
  }

  private renderWithUsers: typeof UsersConsumer.children = ({ users }) => {
    const { section, editable, locale, onDelete } = this.props;
    return (
      <Base id={section.name} className="prevent-from-unselecting-memberships">
        <Row>
          <AvatarWrapper>
            <MembershipSectionCover members={showMembers(users!)} />
          </AvatarWrapper>
          <MembershipHeader>
            <Header
              id={`input-membership-section-name-${section.id}`}
              value={section.name || ""}
              editable={editable}
              component="h3"
              placeholder={i18n.translate("company.members.team_name", { locale })}
              maxLength={30}
              onChange={this.onChangeSectionName}
            />
            {this.renderError()}
            <Description
              value={(section && section.description) || ""}
              editable={editable && Boolean(section)}
              component="p"
              placeholder={i18n.translate("company.members.team_description", { locale })}
              maxLength={140}
              onChange={this.onChangeSectionDescription}
            />
          </MembershipHeader>
        </Row>
        {editable && <MembershipSectionEditPopup onDelete={onDelete} section={section} />}
      </Base>
    );
  };

  private renderError() {
    const { error } = this.props;
    if (!error || !error.name) {
      return null;
    }
    if (error.name === "required") {
      return (
        <ErrorMsg>
          <Translate id="company.members.team_name_required" />
        </ErrorMsg>
      );
    }
    return null;
  }
}

function mapStateToProps(state: RootState): MappedProps {
  return {
    locale: state.auth.locale,
  };
}

export default connect(mapStateToProps)(MembershipSectionHeader);

const Base = styled.div`
  padding: 0px 16px 0px 16px;
  @media ${media.medium} {
    padding: 8px 80px 40px 30px;
  }
`;

const Row = styled.div`
  display: block;
  @media ${media.medium} {
    display: flex;
    flex-direction: row;
  }
`;

const AvatarWrapper = styled.div`
  float: left;
  min-width: 126px;
  width: 126px;
  height: 126px;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 25px;
  @media ${media.medium} {
    float: none;
  }
`;

const MembershipHeader = styled.div`
  display: block;
  @media ${media.medium} {
    flex-grow: 1;
  }
`;

const Header = styled(TogglableStyledInput)`
  width: 100%;
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  outline: none;
`;

const Description = styled(TogglableStyledInput)`
  width: 100%;
  padding-top: 20px;
  min-height: 44px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.69;
  color: rgba(0, 0, 0, 0.27);
  outline: none;
  clear: left;
  @media ${media.medium} {
    padding-top: 10px;
    color: rgba(0, 0, 0, 0.8);
  }
  ${(p: { value: string }) =>
    p.value !== ""
      ? css`
          padding-bottom: 40px;
          @media ${media.medium} {
            padding-bottom: 0;
          }
        `
      : css``};
`;

const ErrorMsg = styled(ErrorMessage)`
  font-size: 13px;
  color: ${colors.red1};
`;



// WEBPACK FOOTER //
// ./frontend/src/company_memberships/components/MembershipSectionHeader.tsx